import {useEffect, useRef} from "react";
import {DocumentNode} from "graphql";
import {useSubscription} from "@apollo/client";
import {useFlags} from "launchdarkly-react-client-sdk";
import {type GraphQLSubscription} from "@aws-amplify/api-graphql";
import {useOktaAuth} from "@okta/okta-react";
import {features} from "../../features";
import {useAmplifyClients} from "../AmplifyClientProvider";
import {AmplifyClientKey, GraphqlVariables} from "./types";

interface UseGraphqlSubscriptionOptions<
  TData,
  TVariables extends GraphqlVariables
> {
  /** Apollo DocumentNode — required during migration, removed after Apollo cleanup */
  document: DocumentNode;
  /** Amplify subscription string */
  amplifySubscription: string;
  /** Variables for the subscription */
  variables?: TVariables;
  /** Which Amplify client to use (defaults to "appsyncBff") */
  amplifyClient?: AmplifyClientKey;
  /** Apollo context (e.g., { endpoint: Endpoints.APPSYNC }) */
  context?: Record<string, unknown>;
  /** Callback when data is received */
  onData?: (data: TData) => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
}

/**
 * Framework-agnostic subscription hook.
 * Routes to Apollo or Amplify based on the migration feature flag.
 *
 * Apollo path: delegates to Apollo's useSubscription hook.
 * Amplify path: uses client.graphql() which returns an Observable,
 * subscribed via useEffect with automatic cleanup on unmount.
 *
 * AppSync subscriptions use AWS_LAMBDA auth — the access token is passed
 * as the authToken so the WebSocket handshake authenticates correctly.
 */
export function useGraphqlSubscription<
  TData = unknown,
  TVariables extends GraphqlVariables = GraphqlVariables
>(options: UseGraphqlSubscriptionOptions<TData, TVariables>): void {
  const flags = useFlags();
  const useAmplify =
    flags[features.dev.amplifyClient.useAmplifyForAllOperations];
  const {authState} = useOktaAuth();

  // === Apollo path ===
  useSubscription<TData, TVariables>(options.document, {
    variables: options.variables as TVariables,
    context: options.context,
    skip: useAmplify,
    onData: ({data: {data}}) => {
      if (data) {
        options.onData?.(data);
      }
    },
    onError: error => {
      options.onError?.(error);
    }
  });

  // === Amplify path ===
  const clients = useAmplifyClients();
  const client = clients?.[options.amplifyClient ?? "appsyncBff"] ?? null;
  // Use refs for callbacks to avoid re-subscribing when callbacks change
  const onDataRef = useRef(options.onData);
  const onErrorRef = useRef(options.onError);
  onDataRef.current = options.onData;
  onErrorRef.current = options.onError;

  // Get the access token for Lambda auth on the WebSocket connection
  const accessToken = authState?.accessToken?.accessToken;

  useEffect(() => {
    if (!useAmplify || !client || !accessToken) return;

    // AppSync subscriptions require Lambda auth with the access token.
    // Without authMode + authToken, the WebSocket connection silently fails.
    const observable = client.graphql<GraphQLSubscription<TData>>({
      query: options.amplifySubscription,
      variables: options.variables as any,
      authMode: "lambda",
      authToken: `Bearer ${accessToken}`
    } as any) as any as import("rxjs").Observable<{data: TData}>;

    const subscription = observable.subscribe({
      next: (message: {data: TData}) => {
        onDataRef.current?.(message.data);
      },
      error: (err: unknown) => {
        const error =
          err instanceof Error ? err : new Error(String(err));
        onErrorRef.current?.(error);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAmplify, client, accessToken, options.amplifySubscription, JSON.stringify(options.variables)]);
}
